<template>
  <Card :has-body-padding-small="true">
    <template #header>
      <h2 class="section-title heading-color">
        More Information
      </h2>
    </template>
    <template #body>
      <ul class="lg:tw-grid lg:tw-grid-cols-3">
        <li
          v-if="hasBaseMidSummit"
          :class="listItemClass"
        >
          <span
            :class="iconClass"
            :style="locationIconStyle"
          />
          <ul>
            <li>{{ elevationSummit }} • Summit</li>
            <li>
              <NuxtLink
                class="hover:tw-underline link-color-brand"
                :to="mapUrl"
              >{{ elevationForecastPoint }} • Forecast Point</NuxtLink>
            </li>
            <li>{{ elevationBase }} • Base</li>
          </ul>
        </li>
        <li
          v-else
          :class="listItemClass"
        >
          <span
            :class="iconClass"
            :style="locationIconStyle"
          />
          <ul>
            <li>Forecast Point</li>
            <li>
              <NuxtLink
                class="hover:tw-underline link-color-brand"
                :to="mapUrl"
              >
                {{ forecastPointDetails }}
              </NuxtLink>
            </li>
          </ul>
        </li>
        <li :class="listItemClass">
          <span
            :class="iconClass"
            :style="updatedIconStyle"
          />
          <span>Updated <strong>{{ timeAgoText }}</strong> by the<br>{{ dataSourceName }}</span>
        </li>
        <li
          v-if="showAttributionDetail"
          :class="listItemClass"
        >
          <span
            :class="iconClass"
            :style="attributionIconStyle"
          />
          <a
            class="hover:tw-underline link-color-brand"
            :href="attributionDetailUrl"
            rel="noopener noreferrer"
            target="_blank"
          >
            How to read this data
          </a>
        </li>
      </ul>
    </template>
  </Card>
</template>

<script>
/* eslint vue/multiline-html-element-content-newline: off */

import { mapState } from 'pinia';
import { formatElevation, getMaskImageStyle } from '@@/utils/CommonUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

export const viewTypes = {
  currentWeather: 'currentWeather',
  forecast: 'forecast',
};

export default {
  name: 'MoreInfoCard',

  props: {
    field: {
      type: String,
      default: null,
    },
    location: {
      type: Object,
      required: true,
    },
    showAttributionDetail: {
      type: Boolean,
      default: true,
    },
    viewType: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapState(useMetaStore, [
      'dataSourcesById',
      'getAttributionDetailUrl',
    ]),

    ...mapState(useUserStore, { units: (state) => state.preferences.units }),

    attributionDetailUrl() {
      return this.getAttributionDetailUrl(this.sourceId, this.field) || '#';
    },

    attributionIconStyle() {
      return {
        backgroundColor: 'currentColor',
        ...getMaskImageStyle('https://blizzard.opensnow.com/icons/fa/regular/circle-question.svg'),
      };
    },

    dataSourceName() {
      return this.dataSourcesById[this.sourceId].name;
    },

    elevationBase() {
      return formatElevation(this.location?.elevation_min, this.units, true);
    },

    elevationForecastPoint() {
      return formatElevation(this.location?.elevation, this.units, true);
    },

    elevationSummit() {
      return formatElevation(this.location?.elevation_max, this.units, true);
    },

    forecastPointDetails() {
      const [lng, lat] = this.location.coordinates.point;
      return `${this.elevationForecastPoint} • ${lat}, ${lng}`;
    },

    hasBaseMidSummit() {
      return typeof this.location?.elevation === 'number'
        && typeof this.location?.elevation_max === 'number'
        && typeof this.location?.elevation_min === 'number';
    },

    iconClass() {
      return 'tw-block tw-h-6 tw-w-6 tw-mr-4';
    },

    listItemClass() {
      return [
        'tw-flex tw-items-center',
        'tw-py-2.5 first:tw-pt-0 lg:first:tw-pt-2.5 last:tw-pb-0 lg:last:tw-pb-2.5',
        'tw-border-b border-color last:tw-border-0 lg:tw-border-0',
        'tw-text-sm',
      ];
    },

    locationIconStyle() {
      return {
        backgroundColor: 'currentColor',
        ...getMaskImageStyle('https://blizzard.opensnow.com/icons/custom/location-pin.svg'),
      };
    },

    updatedIconStyle() {
      return {
        backgroundColor: 'currentColor',
        ...getMaskImageStyle('https://blizzard.opensnow.com/icons/custom/last-updated-arrows.svg'),
      };
    },

    mapUrl() {
      return `/map#${this.location.slug}`;
    },

    sourceId() {
      return this.location.forecast_source_id;
    },

    timeAgoText() {
      return this.$dayjs.utc(this.location.forecast_updated_at).timeAgo();
    },
  },
};
</script>
